import {
  Box,
  CircularProgress,
  Collapse,
  Flex,
  GatsbyImage,
  Link,
  Text,
  useDisclosure,
  Tooltip,
  Picto,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
} from '@stars-ecom/shared-atoms-ui'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { navigate } from 'gatsby'
import capitalize from 'lodash/capitalize'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import merge from 'lodash/merge'
import React, { memo, useContext, useEffect, useState } from 'react'

import { ApiContext, UserContext, WebsiteContext } from '../context'
import { ProductAttachments } from '../product'
import { priceFormat } from '../utils/price'
dayjs.extend(customParseFormat)

const getSereneoDigest = async (customerCode, storeId = '1001', idCommande, numPos, timestamp) => {
  const enc = new TextEncoder() // always utf-8
  let key = await crypto.subtle.digest('SHA-256', enc.encode('3sH75|-|Sr1\\15e1fc4rE'))
  key = Array.from(new Uint8Array(key))
  key = key.map((bytes) => bytes.toString(16).padStart(2, '0')).join('')
  let result = await crypto.subtle.digest(
    'SHA-256',
    enc.encode(
      `Selfcare${customerCode}${storeId}SuiviCommande${idCommande}${numPos}${timestamp}${key}`
    )
  )
  result = Array.from(new Uint8Array(result))
  result = result.map((bytes) => bytes.toString(16).padStart(2, '0')).join('')
  return result
}

const OrderLine = (props) => {
  const { orderLine, details, order, isLoading, trackings, warrantyExtension } = props
  const { isOpen, onToggle } = useDisclosure()
  const [sereneoUrl, setSereneoUrl] = useState()
  const [trackingUrl, setTrackingUrl] = useState()
  const [returnLoader, setReturnLoader] = useState(false)
  const [returnCancelLoader, setReturnCancelLoader] = useState(false) 
  const [chevronOpen, setChevronOpen] = useState(true)
  const timestamp = dayjs().unix()
  const apiContext = useContext(ApiContext)
  const currentUser = useContext(UserContext)
  const websiteContext = useContext(WebsiteContext)
  const getTrackingLink = () => {
    if (isNil(details?.customFields?.trackingcode)) {
      setTrackingUrl(null)
    }
    const transporter = trackings.find((transp) => transp?.code === details?.customFields?.transp && transp.websites.some(w => w.id === websiteContext.id))
    if (isNil(transporter)) {
      setTrackingUrl(null)
    }

    if (details?.customFields?.transp === 'GIR') {
      setTrackingUrl(`${transporter?.url_pattern}${details?.customFields?.trackingcode}.html`)
    } else if (details?.customFields?.transp === 'MLR') {
      setTrackingUrl(
        `${transporter?.url_pattern}${details?.customFields?.trackingcode}&language=FR`
      )
    } else {
      setTrackingUrl(`${transporter?.url_pattern}${details?.customFields?.trackingcode}`)
    }
  }

  const getSereneoUrl = async () => {
    const idCommande = order?.customFields?.externalCode
      ? order?.customFields?.externalCode
      : order?.code

    const digest = await getSereneoDigest(
      currentUser?.customFields?.externalCode,
      process.env.GATSBY_API_CODSOC,
      idCommande,
      orderLine?.customFields?.numPos,
      timestamp
    )
    const environment = process.env.GATSBY_API_STAGE === 'prod' ? '' : 'Preprod-'
    const result = `https://${process.env.GATSBY_SELFCARE_STORE_NAME}.sereneo.com/${environment}Selfcare.kha?contact.canal=Selfcare&contact.selfcare.idClient=${currentUser?.customFields?.externalCode}&contact.selfcare.enseigne=${process.env.GATSBY_API_CODSOC}&contact.selfcare.origine=SuiviCommande&contact.selfcare.idCommande=${idCommande}&contact.selfcare.numeroPoste=${orderLine?.customFields?.numPos}&contact.selfcare.timestamp=${timestamp}&contact.selfcare.key=${digest}&contact.selfcare.pays.code=${process.env.GATSBY_API_COUNTRY_CODE}`
    setSereneoUrl(result)
  }
  useEffect(async () => {
    if (details) {
      getTrackingLink()
    }
  }, [details])

  const returnProducts = async (externalCode) => {
    console.log('order :', order, externalCode);
    const result = await apiContext.AccountApi.getOrderReturnDetail(
      order?.customFields?.externalCode
    )
    const detailsBifrost = result?.lines?.find(
      (product) => product?.customFields?.externalCode == externalCode
    )
    merge(details, detailsBifrost)
    setReturnLoader(false);
    navigate('/productreturn/form', {
      state: { orderLine, details, order }
    })
  }

  const cancelProducts = async (externalCode) => {
    console.log('order :', order, externalCode);
    const result = await apiContext.AccountApi.getOrderReturnDetail(
      order?.customFields?.externalCode
    )
    const detailsBifrost = result?.lines?.find(
      (product) => product?.customFields?.externalCode == externalCode
    )
    merge(details, detailsBifrost)
    setReturnCancelLoader(false);
    navigate('/productcancel/form', {
      state: { orderLine, details, order }
    })
  }

  const getStatusId = (status) => {
    if (isLoading) {
      return 0;
    }
    switch (status?.toLowerCase()) {
      case 'annulé':
      case 'annulé : article épuisé':
        return 5;
      case 'expédié':
      case 'retourné':
        return 4;
      case 'en cours de traitement':
      case 'en cours de préparation':
      case 'en cours de réapprovisionnement':
      case 'en attente de règlement':
        return 3;
      case 'retour en cours de traitement':
      case 'en attente du retour':
        return 2;
      case 'en attente de retour':
        return 1;
      default:
        return 0;
    }
  }

  const circularLoader = (size='48px') => {
    return (
      <Flex align="center">
        <CircularProgress size={size} isIndeterminate color={websiteContext?.mainColor} />
      </Flex>
    )
  }

  const getStatus = (status) => {
    if (isLoading) {
      return circularLoader();
    }
    const state = getStatusId(status);
    switch (state) {
      case 5:
        return (
          <Flex direction="row" align="center">
            <Picto icon="cross" width="20px" height="20px" color="#e41919" />
            <Text color="#e41919" fontWeight={600} ml="10px" fontSize="11px" lineHeight="15px">
              Annulé
            </Text>
          </Flex>
        )
      case 4:
        return (
          <Flex direction="row" align="center">
            <Picto icon="checkLight" width="20px" height="20px" fontWeight="100" color="#3da42f" />
            <Text color="#3da42f" fontWeight={600} ml="10px" fontSize="11px" lineHeight="15px">
              {capitalize(status)}
            </Text>
          </Flex>
        )
      case 2:
      case 3:
        return (
          <Flex direction="row" align="center">
            <Picto icon="clock" width="20px" height="20px" color="#ffa200" />
            <Text
              color="rgb(255, 162, 0)"
              fontWeight={600}
              fontSize="11px"
              lineHeight="15px"
              ml="10px">
              {capitalize(status)}
            </Text>
          </Flex>
        )
      case 1:
        return (
          <Flex direction="row" align="center">
            <Picto icon="check" width="20px" height="20px" color="#ffa200" />
            <Text color="#3da42f" fontWeight={600} ml="10px" fontSize="11px" lineHeight="15px">
              Nous vous avertirons dès que le colis nous sera parvenu.
            </Text>
          </Flex>
        )
      default:
        return (
          <Flex direction="row" align="center">
            <Picto icon="check" width="20px" height="20px" color="#3da42f" />
            <Text color="#3da42f" fontWeight={600} ml="10px" fontSize="11px" lineHeight="15px">
              {capitalize(status)}
            </Text>
          </Flex>
        )
    }
  }

  const getDeliveryDoc = async () => {
    if (details?.customFields?.numLiv) {
      await apiContext?.ExternalServicesApi.getGEDocument(details?.customFields?.numLiv)
    }
  }

  const livEstDate = new Date(details?.customFields?.livEst)
  const today = new Date().getTime()
  const deadLineRetractation = new Date(livEstDate.setDate(livEstDate.getDate() + 14)).getTime()
  const retractationExpired = today >= deadLineRetractation

  return (
    <Box gridGap="2px">
      <Flex
        direction={{ base: 'column', md: 'row' }}
        width="100%"
        backgroundColor="#f6f3f1"
        p="20px"
        justify="space-between">
        <Flex direction="column" width={{ base: '100%', md: '50%' }}>
          <Text
            fontWeight={700}
            mb="20px"
            fontSize="14px"
            lineHeight="30px"
            color="#333"
            display={{ base: 'none', md: 'block' }}>
            Produit
          </Text>
          <Flex direction="row">
            <Flex
              key={`ol_${orderLine?.id}`}
              width="100px"
              height="100px"
              justify="center"
              align="center"
              backgroundColor="#ffffff">
              {orderLine?.productVariant?.product?.featuredAsset?.smallImage?.childImageSharp
                ?.gatsbyImageData ? (
                <GatsbyImage
                  alt={orderLine?.productVariant?.name}
                  image={
                    orderLine?.productVariant?.product?.featuredAsset?.smallImage?.childImageSharp
                      ?.gatsbyImageData
                  }
                />
              ) : (
                orderLine?.productVariant?.customFields?.externalCode
              )}
            </Flex>
            <Flex direction="column" ml="10px">
              <Text
                as="h3"
                fontWeight="700"
                textTransform="uppercase"
                fontSize="14px"
                color="#333"
                lineHeight="20px">
                {orderLine?.productVariant?.product?.name}
              </Text>
              {!isNil(details?.customFields?.offerCode) && (
                <Text
                  fontSize="12px"
                  lineHeight="18px"
                  color="#000">{`Référence : ${details?.customFields?.offerCode}`}</Text>
              )}
              <Text
                fontSize="12px"
                lineHeight="18px"
                color="#000">{`Qté : ${orderLine?.quantity}`}</Text>
              {!isEmpty(warrantyExtension) &&
                warrantyExtension?.map((we) => (
                  <Flex key={we?.productVariant?.customFields?.externalCode}>
                    <Text
                      mt="3px"
                      mb="3px"
                      as="h3"
                      fontWeight="700"
                      textTransform="uppercase"
                      fontSize="12px"
                      color="#333"
                      maxWidth="150px"
                      lineHeight="18px">
                      {` ${we?.productVariant?.product?.name} (${priceFormat(
                        we?.linePriceWithTax
                      )}) `}
                    </Text>
                  </Flex>
                ))}
              <Text
                fontWeight="700"
                textTransform="uppercase"
                fontSize="18px"
                color="#333"
                lineHeight="27px"
                fontFamily="PT Sans narrow, arial, sans-serif">
                {priceFormat(
                  orderLine?.linePriceWithTax + details?.customFields?.ecopart / details?.quantity
                )}
              </Text>
              {!isNil(details?.customFields?.ecopart) && details?.customFields?.ecopart > 0 && (
                <Text fontSize="12px" lineHeight="18px" color="#000" mt="10px">
                  Dont éco-participation : <br />
                  {`${priceFormat(details?.customFields?.ecopart / details?.quantity)}`}
                </Text>
              )}
              <ProductAttachments
                attachments={orderLine?.productVariant?.product?.attachments}
                labelStyle={{
                  fontFamily: 'PT Sans',
                  fontSize: '11px',
                  fontWeight: 300
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex
          direction="column"
          width={{ base: '100%', md: '20%' }}
          alignItems={{ base: 'center', md: 'normal' }}>
          <Text
            fontWeight={700}
            marginBlockEnd="20px"
            fontSize="14px"
            lineHeight="30px"
            color="#333"
            display={{ base: 'none', md: 'block' }}>
            Statut
          </Text>
          {getStatus(details?.customFields?.orderStatus)}
        </Flex>
        <Flex
          direction="column"
          width={{ base: '100%', md: '28%' }}
          mt={{ base: '10px', md: '0px' }}>
          <Text
            fontWeight={700}
            mb="20px"
            fontSize="14px"
            lineHeight="30px"
            color="#333"
            display={{ base: 'none', md: 'block' }}>
            Action
          </Text>
          <Flex direction="column" gridGap="5px" mr={{ base: '0', md: '10px' }}>
            { [1, 2, 4].indexOf(getStatusId(details?.customFields?.orderStatus)) >= 0 &&
              (
              <>
                <Flex
                  direction="row"
                  justifyContent={{ base: 'center', md: 'left' }}
                  p="5px"
                  pl="10px"
                  align="center"
                  cursor="pointer"
                  _hover={{ backgroundColor: 'white' }}
                  height="30px"
                  borderRadius="3px"
                  border="2px solid #dedede"
                  onClick={getDeliveryDoc}>
                  <Picto icon="fileText" width="15px" height="22px" />
                  <Text ml="10px" fontSize="11px">
                    Voir le bon de livraison
                  </Text>
                </Flex>
                <Flex
                  direction="row"
                  justifyContent={{ base: 'center', md: 'left' }}
                  align="center"
                  p="5px"
                  pl="10px"
                  height="30px"
                  cursor="pointer"
                  _hover={{ backgroundColor: 'white' }}
                  borderRadius="3px"
                  border="2px solid #dedede">
                  <Link
                    target="_blank"
                    //onClick={() => track(trackingUrl)}
                    to={trackingUrl}
                    style={{ display: 'flex' }}>
                    <Picto icon="delivery" width="20px" height="20px" />
                    <Text ml="10px" fontSize="11px">
                      Suivre la livraison
                    </Text>
                  </Link>
                </Flex>
                {[4].indexOf(getStatusId(details?.customFields?.orderStatus)) >= 0 &&
                  <>
                    <Flex
                      direction="row"
                      justifyContent={{ base: 'center', md: 'left' }}
                      onClick={() => {
                        if (!returnLoader) {
                          setReturnLoader(true);
                          returnProducts(orderLine?.productVariant?.customFields?.externalCode)
                        }
                      }}
                      p="5px"
                      pl="10px"
                      align="center"
                      height="30px"
                      cursor="pointer"
                      _hover={{ backgroundColor: 'white' }}
                      borderRadius="3px"
                      border="2px solid #dedede"
                      >
                        {!!returnLoader ? 
                          circularLoader('20px')
                        :
                        <>
                          <Picto icon="reply" width="15px" height="15px" />
                          <Text ml="10px" fontSize="11px">
                            Retourner un produit
                          </Text>
                        </>
                      }
                    </Flex>
                    <Popover isOpen={retractationExpired ? undefined : false} placement="bottom-end" trigger="hover" pointerEvents="none" disabled preventOverflow={false} flip={false} arrowSize={12}>
                      <PopoverTrigger>
                        <Flex
                          direction="row"
                          justifyContent={{ base: 'center', md: 'left' }}
                          onClick={() => {
                            if (!retractationExpired && !returnCancelLoader) {
                              setReturnCancelLoader(true);
                              cancelProducts(orderLine?.productVariant?.customFields?.externalCode)
                            }
                          }}
                          p="5px"
                          pl="10px"
                          align="center"
                          height="30px"
                          cursor={retractationExpired ? 'not-allowed' : 'pointer'}
                          opacity={retractationExpired ? 0.4 : 1}
                          _hover={{ backgroundColor: 'white' }}
                          borderRadius="3px"
                          border="2px solid #dedede"
                          >
                          {!!returnCancelLoader ? circularLoader('20px') :
                            <>
                              <Picto icon="deliveryBack" width="20px" height="20px" />
                              <Text ml="10px" fontSize="11px">
                                Se rétracter
                              </Text>
                            </>
                          }
                        </Flex>
                      </PopoverTrigger>
                      <PopoverContent
                        p="10px"
                        borderRadius="0"
                        borderColor={websiteContext?.mainColor}
                        bg="#fff"
                        style={{ width: '330px', marginRight: '-30px', boxShadow: 'none' }}>
                          <PopoverArrow
                            border="1px"
                            borderColor={`${websiteContext?.mainColor} #fff #fff ${websiteContext?.mainColor}`}
                            marginLeft="60px"
                            marginTop="-.5px"
                          />
                          <PopoverCloseButton style={{ marginTop: '2px' }} _hover={{ bg: 'transparent' }} />
                          <Text fontSize="14px" m="8px 4px">Le délai de rétractation de 14 jours pour ce produit est dépassé. 
                            Si vous avez besoin d'aide ou d'informations complémentaires, notre service client reste à votre disposition via le <Link  style={{ textDecoration: 'underline', color: websiteContext?.mainColor }} to={`/contacts`}>formulaire de contact.</Link>
                          </Text>
                      </PopoverContent>
                    </Popover>
                  </>
                }
              </>
            )}
            { [1, 2, 3, 4, 5].indexOf(getStatusId(details?.customFields?.orderStatus)) >= 0 &&
              (
              <Flex
                direction="row"
                justifyContent={{ base: 'center', md: 'left' }}
                p="5px"
                pl="10px"
                align="center"
                height="30px"
                cursor="pointer"
                _hover={{ backgroundColor: 'white' }}
                borderRadius="3px"
                onClick={() => {
                  onToggle()
                  setChevronOpen(!chevronOpen)
                  getSereneoUrl()
                }}
                border="2px solid #dedede">
                <Picto
                  icon={chevronOpen ? 'chevronDown' : 'chevronUp'}
                  width="15px"
                  height="15px"
                />
                <Text ml="10px" fontSize="11px">
                  Où est mon colis ?
                </Text>
              </Flex>
              )
            }
          </Flex>
        </Flex>
      </Flex>
      <Box padding="20px" pt="0px" backgroundColor="#f6f3f1">
        <Collapse in={isOpen} animateOpacity backgroundColor="#f6f3f1">
          <Box rounded="md" shadow="md">
            {sereneoUrl ? (
              <iframe width="100%" height="100%" src={sereneoUrl} title="Suivi commande" />
            ) : (
              <CircularProgress isIndeterminate color={websiteContext?.mainColor} />
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

export default memo(OrderLine)
